import { readableInk } from './contrastInk.js';

/**
 * A stable colour per machine.
 *
 * The factory has eight lines and people talk about them by name — "it's on the
 * L120", "ath600 is down". Machine Monitor already gave each one a colour, on
 * its cards, and every other screen printed the name in the same grey badge as
 * every other machine. So the one identifier a planner scans a table for was
 * the one thing with no visual handle on it.
 *
 * Shared rather than copied, for the same reason `clientColour` is: the L120
 * being orange on one screen and blue on the next would be worse than no colour
 * at all.
 *
 * Text colour is measured rather than fixed at white — see contrastInk.js.
 * White on the l75 blue is 2.03:1, and a label nobody can read is not an
 * improvement on grey.
 */

const MACHINE_COLOURS = {
  l75: '#3874ff',
  l70: '#0fc6c2',
  l120: '#e5780b',
  dtb1250: '#8b5cf6',
  skip2250: '#f43f5e',
  ach300: '#10b981',
  dhc600: '#f59e0b',
  ath600: '#6366f1'
};

// Anything not in the list still gets a colour of its own rather than falling
// back to the grey this exists to remove. New machines arrive; they should look
// like machines the day they are plugged in, not like missing data.
const FALLBACK = [
  '#0ea5e9',
  '#a855f7',
  '#84cc16',
  '#ec4899',
  '#14b8a6',
  '#f97316'
];

const key = id => String(id || '').trim().toLowerCase();

export function machineColour(id) {
  const k = key(id);
  if (!k) return null;
  if (MACHINE_COLOURS[k]) return MACHINE_COLOURS[k];

  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return FALLBACK[h % FALLBACK.length];
}

/** Inline style for a solid machine badge, readable on either theme. */
export function machineBadgeStyle(id) {
  const bg = machineColour(id);
  if (!bg) return undefined;
  return {
    backgroundColor: bg,
    color: readableInk(bg),
    border: '1px solid rgba(0, 0, 0, 0.15)'
  };
}

export { MACHINE_COLOURS };
